import React from 'react';
import { graphql } from 'gatsby';

import Layout from '@components/Layout';
import Breadcrumbs from '@components/Breadcrumbs';
import Hero from '@components/Hero';
import { Grid, Row, Col } from '@components/Grid';

import styles from './styles.module.scss';
import Card from '@components/Card';
import { Divider } from '@components/Grid';
import SEO from '@components/SEO';
import StructuredData from '@components/StructuredData';
import { SITE_URL, stripHtml, SPA_BUSINESS_REF } from '@helpers/schema';
import Button from '@components/Button';

const Treatments = ({
    data: {
        page: {
            frontmatter: {
                path,
                name,
                seoTitle,
                seoDescription,
                buttonText,
                buttonLink,
                description,
                terms,
                image,
                items,
            },
        },
    },
}) => {
    const treatmentItems = (items || []).flatMap(item =>
        (item?.prices || []).map(p => ({
            name: item?.prices?.length > 1 && p?.name ? `${item.name} (${p.name})` : item?.name,
            description: stripHtml(item?.description),
            price: p?.price,
        }))
    );

    return (
        <Layout>
            <SEO title={seoTitle} description={seoDescription} />
            <StructuredData
                schema={[
                    {
                        '@context': 'https://schema.org',
                        '@type': 'Service',
                        '@id': `${SITE_URL}${path}#service`,
                        name,
                        description: stripHtml(description),
                        url: `${SITE_URL}${path}`,
                        provider: SPA_BUSINESS_REF,
                        serviceType: 'Spa Treatment',
                    },
                    ...treatmentItems.map((t, i) => ({
                        '@context': 'https://schema.org',
                        '@type': 'Offer',
                        '@id': `${SITE_URL}${path}#offer-${i + 1}`,
                        name: t.name,
                        ...(t.description && { description: t.description }),
                        ...(t.price && {
                            price: String(t.price).replace(/[^0-9.]/g, ''),
                            priceCurrency: 'GBP',
                        }),
                        availability: 'https://schema.org/InStock',
                        itemOffered: {
                            '@type': 'Service',
                            '@id': `${SITE_URL}${path}#service`,
                        },
                    })),
                ]}
            />

            <Breadcrumbs>
                <Breadcrumbs.Breadcrumb title="Spa" link="/spa" />
                <Breadcrumbs.Breadcrumb title={name} link={path} />
            </Breadcrumbs>

            <Hero image={image}>
                <h1>{name}</h1>
                <p>{description}</p>

                {!!buttonText && !!buttonLink && (
                    <>
                        <br />
                        <Button link={buttonLink}>{buttonText}</Button>
                    </>
                )}
            </Hero>

            <Grid>
                <Row>
                    {items.map(({ name, description, prices, extras = [] }) => (
                        <Col xs={12} key={name}>
                            <div className={styles.treatment}>
                                <div className={styles.treatmentContent}>
                                    <p className={styles.treatmentTitle}>{name}</p>
                                    <p className={styles.treatmentDescription}>{description}</p>

                                    {extras && !!extras.length && (
                                        <div className={styles.treatmentExtras}>
                                            {extras.map(extra => (
                                                <span>{extra?.extra}</span>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <span className={styles.treatmentDivider} />

                                <div className={styles.treatmentPrices}>
                                    {prices.map(({ name = '', price = '' }) => (
                                        <div
                                            key={`${name}-${price}`}
                                            className={styles.treatmentPricesPrice}
                                        >
                                            {name && (
                                                <span className={styles.treatmentPricesPriceLabel}>
                                                    {name}
                                                </span>
                                            )}
                                            {price ? (
                                                <span className={styles.treatmentPricesPriceValue}>
                                                    {`£${price}`}
                                                </span>
                                            ) : (
                                                <span
                                                    className={
                                                        styles.treatmentPricesPriceValueEmpty
                                                    }
                                                />
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </Col>
                    ))}
                </Row>

                {terms && (
                    <Row>
                        <Col xs>
                            <Divider />
                            <Card>
                                <Card.Content>
                                    <p>{terms}</p>
                                </Card.Content>
                            </Card>
                        </Col>
                    </Row>
                )}
            </Grid>
        </Layout>
    );
};

export default Treatments;

export const query = () => graphql`
    query TreatmentsQuery($path: String!) {
        page: markdownRemark(frontmatter: { path: { eq: $path } }) {
            frontmatter {
                path
                name: treatment_name
                seoTitle: treatment_seo_title
                seoDescription: treatment_seo_description
                buttonText: treatment_button_text
                buttonLink: treatment_button_link
                description: treatment_description
                terms: treatment_terms
                image: treatment_image {
                    childImageSharp {
                        fluid(maxHeight: 500) {
                            ...GatsbyImageSharpFluid
                        }
                    }
                }
                items: treatment_items {
                    name: treatment_items_name
                    description: treatment_items_description
                    prices: treatment_items_prices {
                        name: treatment_items_prices_name
                        price: treatment_items_prices_price
                    }
                    extras: treatment_items_extras {
                        extra: treatment_items_extras_extra
                    }
                }
            }
        }
    }
`;
